/* eslint-disable import/no-cycle */
/* eslint-disable class-methods-use-this */
import { makeObservable, action, computed, observable, runInAction } from 'mobx';
import {
  PaymentMethod,
  PaymentMethodService,
  IdentifierService,
  PaymentType,
  PaymentCardStatus
} from '../../swagger/api';
import BaseStore from '../base-stores/BaseStore';
import { RootStore } from '../RootStore';

export const PAYMENT_CARDS_PAGE_SIZE = 15;

export type PaymentCardList = {
  cards: PaymentMethod[];
  pageNumber: number;
  filteredCount: number;
};

const createPaymentCardList = (): PaymentCardList => ({
  cards: [],
  pageNumber: 1,
  filteredCount: 0
});

export default class AdministrationPaymentCardsStore extends BaseStore {
  myPaymentCards: PaymentCardList = createPaymentCardList();

  othersPaymentCards: PaymentCardList = createPaymentCardList();

  validPaymentCardsOwnedByLoggedInPerson: PaymentMethod[] = [];

  paymentCardSelectedInDropdown?: PaymentMethod;

  refreshRequests = observable.set<string>();

  getProductsOnPaymentCard = (paymentCardId: string) => {
    const promise = IdentifierService.getIdentifiersByPaymentMethod({
      paymentMethodId: paymentCardId
    });
    return promise;
  };

  requestRefreshForCards = (ids: string[]) => {
    ids.forEach((id) => this.refreshRequests.add(id));
  };

  clearRefreshRequest = (id: string) => {
    this.refreshRequests.delete(id);
  };

  isValidPaymentCard = (paymentMethod: PaymentMethod) =>
    paymentMethod.type === PaymentType.CreditCard &&
    (paymentMethod.paymentCardStatus === PaymentCardStatus.ValidCard ||
      paymentMethod.paymentCardStatus === PaymentCardStatus.CardAboutToExpire);

  getValidPaymentCardsOwnedByLoggedInPerson = () => {
    const promise = PaymentMethodService.getPaymentMethodsByPersonId({
      personId: this.rootStore.userStore.person?.id!
    });
    promise.then((result: PaymentMethod[]) => {
      runInAction(() => {
        this.validPaymentCardsOwnedByLoggedInPerson = result.filter(
          (paymentMethod: PaymentMethod) => this.isValidPaymentCard(paymentMethod)
        );
      });
    });
    return promise;
  };

  get paymentCards(): PaymentMethod[] {
    return [...this.myPaymentCards.cards, ...this.othersPaymentCards.cards];
  }

  private getPaginatedPaymentCards = (isPaymentAgreementOwner: boolean, pageNumber: number) => {
    this.rootStore.filterStore.paymentCardsFilterStore.setRequestParams();
    const params = this.rootStore.filterStore.paymentCardsFilterStore.requestParams;
    params.accountId = this.rootStore.userStore.currentAccount?.id!;
    return PaymentMethodService.getPaginatedCreditCardPaymentMethodsByAccountId({
      accountId: params.accountId,
      paymentAgreementOwnerPersonId: this.rootStore.userStore.person?.id,
      isPaymentAgreementOwner,
      customCardSorting: true,
      cardStatuses: params.cardStatus,
      searchString: params.searchString,
      pageNumber,
      pageSize: PAYMENT_CARDS_PAGE_SIZE
    });
  };

  private loadPaymentCardList = (listKey: 'myPaymentCards' | 'othersPaymentCards') => {
    const isPaymentAgreementOwner = listKey === 'myPaymentCards';
    const promise = this.getPaginatedPaymentCards(
      isPaymentAgreementOwner,
      this[listKey].pageNumber
    );
    promise.then((result) => {
      runInAction(() => {
        this[listKey] = {
          ...this[listKey],
          cards: result.paginatedItems ?? [],
          filteredCount: result.filteredCount ?? 0
        };
      });
    });
    return promise;
  };

  getMyPaymentCards = () => this.loadPaymentCardList('myPaymentCards');

  getOthersPaymentCards = () => {
    if (this.rootStore.filterStore.paymentCardsFilterStore.showOnlyMyCards) {
      this.othersPaymentCards = createPaymentCardList();
      return Promise.resolve();
    }
    return this.loadPaymentCardList('othersPaymentCards');
  };

  getPaymentCardsInfo = () => Promise.all([this.getMyPaymentCards(), this.getOthersPaymentCards()]);

  setPaymentCardSelectedInDropdown = (selectedCard: PaymentMethod | undefined) => {
    this.paymentCardSelectedInDropdown = selectedCard;
  };

  setMyPaymentCardsPage = (pageNumber: number) => {
    this.myPaymentCards.pageNumber = pageNumber;
  };

  setOthersPaymentCardsPage = (pageNumber: number) => {
    this.othersPaymentCards.pageNumber = pageNumber;
  };

  resetPages = () => {
    this.myPaymentCards.pageNumber = 1;
    this.othersPaymentCards.pageNumber = 1;
  };

  constructor(rootStore: RootStore) {
    super(rootStore);
    makeObservable(this, {
      myPaymentCards: observable,
      othersPaymentCards: observable,
      paymentCards: computed,
      refreshRequests: observable,
      paymentCardSelectedInDropdown: observable,
      validPaymentCardsOwnedByLoggedInPerson: observable,
      requestRefreshForCards: action,
      getValidPaymentCardsOwnedByLoggedInPerson: action,
      getMyPaymentCards: action,
      getOthersPaymentCards: action,
      getPaymentCardsInfo: action,
      getProductsOnPaymentCard: action,
      setMyPaymentCardsPage: action,
      setOthersPaymentCardsPage: action,
      resetPages: action,
      setPaymentCardSelectedInDropdown: action
    });
  }
}
